/**
 * FoundersList — admin queue of all founders, bucketed by current phase.
 *
 * Replaces the old `/dashboard/founder-vetting` queue.  Each row links to
 * the unified `/dashboard/founders/:userId` timeline where the admin
 * takes action.
 *
 * Buckets surface as horizontal pill filters; default view is "all" with
 * a phase-grouped accordion.  Counts are live.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Send, Sparkles, ShieldCheck, Rocket, Brain, Compass, FileSignature,
  ChevronRight, Search, RefreshCw, Filter, FileText, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import LinkedInButton from '@/components/LinkedInButton';

const PHASE_ICON = {
  applied: Send,
  ai_council: Sparkles,
  human_decision: ShieldCheck,
  onboarded: Rocket,
  ai_qa: Brain,
  psychometric: Compass,
  nda_signed: FileSignature,
  mou_active: FileSignature,
};

const PHASE_TINT = {
  applied:        'bg-slate-50 text-slate-700 border-slate-200',
  ai_council:     'bg-violet-50 text-violet-700 border-violet-200',
  human_decision: 'bg-[#FFF8E5] text-[#5E1F8B] border-[#8B3FC5]',
  onboarded:      'bg-sky-50 text-sky-700 border-sky-200',
  ai_qa:          'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  psychometric:   'bg-amber-50 text-amber-700 border-amber-200',
  nda_signed:     'bg-teal-50 text-teal-700 border-teal-200',
  mou_active:     'bg-emerald-50 text-emerald-700 border-emerald-200',
};

function fmtAgo(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
  return d.toLocaleDateString();
}

function PhaseBadge({ id, label }) {
  const Icon = PHASE_ICON[id];
  return (
    <span
      data-testid={`founders-phase-badge-${id}`}
      className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded border inline-flex items-center gap-1 ${PHASE_TINT[id] || 'bg-slate-50 text-slate-700 border-slate-200'}`}
    >
      {Icon && <Icon size={11}/>} {label}
    </span>
  );
}

function FounderRow({ r, phaseLabel }) {
  return (
    <div
      data-testid={`founder-row-${r.user_id}`}
      className="ve-card p-4 hover:border-[#8B3FC5] transition-colors group"
    >
      <div className="flex items-center justify-between gap-3">
        <Link
          to={`/dashboard/founders/${r.user_id}`}
          data-testid={`founder-row-link-${r.user_id}`}
          className="flex-1 min-w-0"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <PhaseBadge id={r.current_phase_id} label={phaseLabel}/>
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-mono">
              {fmtAgo(r.applied_at)}
            </span>
            {r.blocked > 0 && (
              <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded">
                {r.blocked} blocked
              </span>
            )}
            {r.leadership_index != null && (
              <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] bg-[#FFF8E5] border border-[#8B3FC5]/40 px-1.5 py-0.5 rounded">
                Lead · {Math.round(r.leadership_index)}
              </span>
            )}
          </div>
          <div className="mt-1.5 flex items-baseline gap-2">
            <h3 className="font-bold text-sm">{r.name || r.email}</h3>
            <LinkedInButton url={r.linkedin_url} size={11}/>
            {r.startup?.name && (
              <span className="text-xs text-slate-500">· {r.startup.name}</span>
            )}
            <LinkedInButton url={r.startup?.linkedin_url} size={10} label="Startup LinkedIn"/>
            {r.startup?.industry && (
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{r.startup.industry}</span>
            )}
          </div>
          <div className="mt-1 text-[11px] text-slate-500 truncate">{r.email}</div>
        </Link>
        <div className="flex items-center gap-3 shrink-0">
          {r.latest_report_id && (
            <Link
              to={`/dashboard/psycho-reports/${r.latest_report_id}`}
              data-testid={`founder-row-report-${r.user_id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1.5 rounded border border-[#8B3FC5] text-[#5E1F8B] hover:bg-[#FFF8E5] inline-flex items-center gap-1"
              title="Open psychometric report"
            >
              <FileText size={11}/> Report
            </Link>
          )}
          <Link
            to={`/dashboard/founders/${r.user_id}`}
            className="text-right shrink-0 flex items-center gap-2"
          >
            <div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 font-bold">Progress</div>
              <div className="display text-base text-[#8B3FC5] font-bold">{r.percent}%</div>
            </div>
            <ChevronRight size={14} className="text-slate-300 group-hover:text-[#8B3FC5] transition-colors"/>
          </Link>
        </div>
      </div>
    </div>
  );
}

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect. */
export default function FoundersList() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'system_admin' || user?.role === 'holding_admin';

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/founder-timeline/');
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load founders.');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="ve-card p-10 text-center">
        <h2 className="display text-2xl">Restricted</h2>
        <p className="text-sm text-slate-600 mt-2">Founders list is for system or holding admins.</p>
        <Link to="/dashboard/founder" className="ve-btn-primary mt-4 inline-flex">
          View your own journey →
        </Link>
      </div>
    );
  }

  if (loading || !data) return <div className="text-sm text-slate-500">Loading founders…</div>;

  // Filter
  const lq = q.toLowerCase().trim();
  const rows = data.rows.filter((r) => {
    if (phase !== 'all' && r.current_phase_id !== phase) return false;
    if (!lq) return true;
    return [r.name, r.email, r.company, r.startup?.name, r.startup?.industry]
      .filter(Boolean).join(' ').toLowerCase().includes(lq);
  });

  // Counts per phase for the filter pill labels
  const counts = {};
  data.phases.forEach((p) => { counts[p.id] = (data.buckets[p.id] || []).length; });
  const phaseLabelMap = {}; data.phases.forEach((p) => { phaseLabelMap[p.id] = p.label; });

  return (
    <div data-testid="founders-list">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">Founders · M3 Holdings</div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
            All founders. <span className="ve-gold-text">One timeline.</span>
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            From application through MOU. Click any founder to open their unified timeline and act on the active phase.
          </p>
        </div>
        <div className="flex gap-2 self-start">
          <Link to="/dashboard/psycho-cohort"
                data-testid="founders-cohort-link"
                className="ve-btn-secondary text-xs">
            <BarChart3 size={12}/> Cohort analytics
          </Link>
          <button onClick={load} className="ve-btn-secondary text-xs"
                  data-testid="founders-refresh">
            <RefreshCw size={12}/> Refresh
          </button>
        </div>
      </div>

      {/* Filter pills */}
      <div className="mt-6 flex flex-wrap gap-2 items-center" data-testid="founders-filter-bar">
        <button
          onClick={() => setPhase('all')}
          data-testid="founders-filter-all"
          className={`text-[11px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded border ${
            phase === 'all'
              ? 'bg-[#0A0A0A] text-[#8B3FC5] border-[#0A0A0A]'
              : 'bg-white text-slate-700 border-slate-200 hover:border-[#8B3FC5]'
          }`}
        >
          All · {data.total}
        </button>
        {data.phases.map((p) => (
          <button
            key={p.id}
            onClick={() => setPhase(p.id)}
            data-testid={`founders-filter-${p.id}`}
            className={`text-[11px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded border inline-flex items-center gap-1.5 ${
              phase === p.id
                ? 'bg-[#0A0A0A] text-[#8B3FC5] border-[#0A0A0A]'
                : 'bg-white text-slate-700 border-slate-200 hover:border-[#8B3FC5]'
            }`}
          >
            {p.label}
            <span className="text-[9px] font-mono opacity-70">{counts[p.id] || 0}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mt-4 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, email, startup, industry…"
          data-testid="founders-search"
          className="w-full text-sm rounded-md border border-slate-200 pl-9 pr-3 py-2 focus:outline-none focus:border-[#8B3FC5]"
        />
      </div>

      {/* Rows */}
      <div className="mt-6 space-y-2">
        {rows.length === 0 && (
          <div className="ve-card p-10 text-center text-sm text-slate-500" data-testid="founders-empty">
            <Filter size={20} className="mx-auto text-slate-300 mb-3"/>
            No founders match the current filter.
          </div>
        )}
        {rows.map((r) => (
          <FounderRow
            key={r.user_id}
            r={r}
            phaseLabel={phaseLabelMap[r.current_phase_id] || 'Unknown'}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="mt-8 text-xs text-slate-500 italic">
        Replaces the legacy <code className="font-mono">/dashboard/founder-vetting</code> queue.
        Each row&rsquo;s &ldquo;Now: &hellip;&rdquo; reflects the current actionable phase derived live from source-of-truth collections.
      </div>
    </div>
  );
}
